/**
 * Redux slice pour l'authentification des clients publics
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { clientAuthApi, ClientUser, RegisterAnefData, LoginData } from '../../services/clientApi';

interface ClientAuthState {
  user: ClientUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  emailVerificationSent: boolean;
  passwordResetSent: boolean;
}

// Charger l'état depuis localStorage au démarrage
const loadStoredUser = (): ClientUser | null => {
  const storedUser = localStorage.getItem('clientUser');
  if (storedUser) {
    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  }
  return null;
};

const initialState: ClientAuthState = {
  user: loadStoredUser(),
  isAuthenticated: !!localStorage.getItem('clientAccessToken'),
  isLoading: false,
  error: null,
  emailVerificationSent: false,
  passwordResetSent: false,
};

// ========================================
// THUNKS ASYNC
// ========================================

// Inscription via ANEF
export const registerViaAnef = createAsyncThunk(
  'clientAuth/registerViaAnef',
  async (data: RegisterAnefData, { rejectWithValue }) => {
    try {
      const response = await clientAuthApi.registerViaAnef(data);
      const { accessToken, refreshToken, user } = response.data;

      // Sauvegarder les tokens et l'utilisateur pour connecter automatiquement
      if (accessToken && refreshToken) {
        localStorage.setItem('clientAccessToken', accessToken);
        localStorage.setItem('clientRefreshToken', refreshToken);
        localStorage.setItem('clientUser', JSON.stringify(user));
      }

      return { user, accessToken, refreshToken };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  }
);

// Connexion
export const login = createAsyncThunk(
  'clientAuth/login',
  async (data: LoginData, { rejectWithValue }) => {
    try {
      console.log('[ClientAuthSlice] Starting login request...');
      const response = await clientAuthApi.login(data);
      console.log('[ClientAuthSlice] Login response received:', {
        status: response.status,
        hasData: !!response.data
      });

      const { accessToken, refreshToken, user } = response.data;
      console.log('[ClientAuthSlice] Extracted from response:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasUser: !!user,
        userRole: user?.role,
        userType: (user as any)?.user_type
      });

      // Sauvegarder les tokens et l'utilisateur
      console.log('[ClientAuthSlice] Saving to localStorage...');
      localStorage.setItem('clientAccessToken', accessToken);
      localStorage.setItem('clientRefreshToken', refreshToken);
      localStorage.setItem('clientUser', JSON.stringify(user));

      // Vérifier immédiatement que c'est bien stocké
      const storedToken = localStorage.getItem('clientAccessToken');
      const storedUser = localStorage.getItem('clientUser');
      console.log('[ClientAuthSlice] Verification after storage:', {
        storedTokenLength: storedToken?.length || 0,
        storedTokenPreview: storedToken?.substring(0, 20) + '...',
        storedUserExists: !!storedUser,
        storedUserData: storedUser ? JSON.parse(storedUser) : null
      });

      return user;
    } catch (error: any) {
      console.error('[ClientAuthSlice] Login error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Email ou mot de passe incorrect');
    }
  }
);

// Déconnexion
export const logout = createAsyncThunk(
  'clientAuth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem('clientRefreshToken');
      if (refreshToken) {
        await clientAuthApi.logout(refreshToken);
      }

      // Supprimer les tokens et l'utilisateur
      localStorage.removeItem('clientAccessToken');
      localStorage.removeItem('clientRefreshToken');
      localStorage.removeItem('clientUser');

      return null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la déconnexion');
    }
  }
);

// Récupérer le profil
export const fetchProfile = createAsyncThunk(
  'clientAuth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await clientAuthApi.getProfile();
      return response.data.user;
    } catch (error: any) {
      // Si erreur 401, déconnecter
      if (error.response?.status === 401) {
        localStorage.removeItem('clientAccessToken');
        localStorage.removeItem('clientRefreshToken');
      }
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération du profil');
    }
  }
);

// Vérifier l'email
export const verifyEmail = createAsyncThunk(
  'clientAuth/verifyEmail',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await clientAuthApi.verifyEmail(token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Token de vérification invalide');
    }
  }
);

// Mot de passe oublié
export const forgotPassword = createAsyncThunk(
  'clientAuth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await clientAuthApi.forgotPassword(email);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'envoi de l\'email');
    }
  }
);

// Réinitialiser le mot de passe
export const resetPassword = createAsyncThunk(
  'clientAuth/resetPassword',
  async ({ token, newPassword }: { token: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await clientAuthApi.resetPassword(token, newPassword);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la réinitialisation');
    }
  }
);

// ========================================
// SLICE
// ========================================

const clientAuthSlice = createSlice({
  name: 'clientAuth',
  initialState,
  reducers: {
    // Réinitialiser l'erreur
    clearError: (state) => {
      state.error = null;
    },

    // Réinitialiser l'état
    resetAuthState: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.emailVerificationSent = false;
      state.passwordResetSent = false;
    },

    // Mettre à jour l'utilisateur
    updateUser: (state, action: PayloadAction<Partial<ClientUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Inscription via ANEF
    builder.addCase(registerViaAnef.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(registerViaAnef.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.emailVerificationSent = true;
      state.error = null;
    });
    builder.addCase(registerViaAnef.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Connexion
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
      state.error = null;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Déconnexion
    builder.addCase(logout.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.isLoading = false;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    });
    builder.addCase(logout.rejected, (state) => {
      // Même en cas d'erreur, on déconnecte côté client
      state.isLoading = false;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem('clientAccessToken');
      localStorage.removeItem('clientRefreshToken');
    });

    // Récupérer le profil
    builder.addCase(fetchProfile.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
    });
    builder.addCase(fetchProfile.rejected, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
    });

    // Vérifier l'email
    builder.addCase(verifyEmail.fulfilled, (state) => {
      if (state.user) {
        state.user.email_verified = true;
      }
    });

    // Mot de passe oublié
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.passwordResetSent = true;
    });

    // Réinitialiser le mot de passe
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.passwordResetSent = false;
    });
  },
});

export const { clearError, resetAuthState, updateUser } = clientAuthSlice.actions;

export default clientAuthSlice.reducer;
